import { usePostHog } from 'posthog-js/react';
import { trackEvent as telemetryTrackEvent } from '@utils/analytics/telemetry';
import { TRACK_ERROR_NAMES, TRACK_EVENT_NAMES } from '@utils/analytics/events';
import { getPlatform } from '@utils/analytics/platform';

// Re-export para no romper los imports existentes (`import useTracking, { TRACK_EVENT_NAMES } ...`).
export { TRACK_EVENT_NAMES, TRACK_ERROR_NAMES };

type EventData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

const getLoginData = () => {
  return {
    commerceId: localStorage.getItem('id_commerce'),
    id: localStorage.getItem('id_user'),
    pathId: localStorage.getItem('id_path'),
    commerceName: localStorage.getItem('commerce_name'),
    commerceLogo: localStorage.getItem('commerce_logo'),
    userName: localStorage.getItem('user_name'),
    userPermissions: localStorage.getItem('user_permissions'),
    userData: localStorage.getItem('userData') ?? '',
  };
};

// `localStorage.userData` no siempre es JSON: puede venir truncado (escritura abortada por
// QuotaExceeded) o con un valor legado tipo '[object Object]'. Sin guard, el JSON.parse lanza
// DENTRO del handler que llamó a trackEvent y mata la acción del usuario — un botón muerto por
// un fallo de telemetría. Se exporta para poder anclarlo con tests.
export const parseStoredUserData = (raw: string | null | undefined): EventData | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as EventData) : null;
  } catch {
    return null;
  }
};

const defaultEventData = () => {
  const loginData = getLoginData();
  const user = parseStoredUserData(loginData.userData);

  return {
    ...loginData,
    ...(user && {
      user: {
        id: user.id,
        email: user.email,
        name: user.name,
        status: user.status,
        username: user.username,
      },
    }),
  };
};

const useTracking = () => {
  const posthog = usePostHog();

  // El pageview se centralizó en usePageView (montado en cada Layout) para no
  // multiplicarse por cada componente que usa useTracking. La captura del session id
  // de telemetría vive en AuthContext.

  const trackEvent = (eventName: string, eventData?: EventData) => {
    const defaultData = defaultEventData();
    // Tipado explícito: el payload es abierto por diseño (abajo se le añaden distinct_id,
    // id_commerce, userId…). Antes se apoyaba en el `any` que devolvía el JSON.parse sin guard;
    // al tiparlo, la firma de índice tiene que declararse.
    const data: EventData = {
      ...defaultData,
      // Plataforma por evento (web | electron) para segmentar cualquier evento, no solo el perfil.
      platform: getPlatform(),
      ...eventData,
    };

    if (defaultData.user) {
      data.distinct_id = defaultData.commerceName;
      data.id_commerce = defaultData.commerceId;
      data.userId = defaultData.user.id;
      data.userEmail = defaultData.user.email;
      data.userName = defaultData.user.username;
    }

    // Fan-out a PostHog (payload completo) + Clarity (evento + upgrade) + GA4 (snake_case, sin PII).
    telemetryTrackEvent(posthog, eventName, data);
  };

  return {
    trackEvent,
  };
};

export default useTracking;
